import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { clientsApi } from "../../services/businessApi";
import { shareLinkApi } from "../../services/shareLinkApi";
import { useToast } from "../../context/ToastContext";
import { Card } from "../../components/common/Card";
import { PageHeader } from "../../components/common/PageHeader";
import { Button } from "../../components/common/Button";
import { DataTable } from "../../components/common/DataTable";
import { Modal } from "../../components/common/Modal";
import { Field } from "../../components/common/Field";
import { Pager } from "../../components/common/Pager";

export function ClientDetailPage() {
  const { clientId } = useParams();
  const { notify } = useToast();
  const [transactions, setTransactions] = useState(null);
  // #329/#372: this list used to arrive whole, however long it was.
  // Paging it without saying so would be worse than not paging it --
  // 50 of 800 looks exactly like all 50.
  const [total, setTotal] = useState(null);
  const [pageLimit, setPageLimit] = useState(null);
  const [pageOffset, setPageOffset] = useState(0);
  const [shareLinks, setShareLinks] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), pounds: "", income_amount: "", price_per_pound: "" });

  async function loadTransactions(nextOffset = 0) {
    const r = await clientsApi.transactions(clientId, { offset: nextOffset });
    setTransactions(r.transactions);
    setPageOffset(nextOffset);
    // Null when an older API answers without paging fields. Pager renders
    // nothing rather than claiming a page count it was not given.
    setTotal(r.total ?? null);
    setPageLimit(r.limit ?? null);
  }

  async function loadShareLinks() {
    const r = await shareLinkApi.list(clientId);
    setShareLinks(r.share_links || []);
  }

  useEffect(() => { loadTransactions(); loadShareLinks(); }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function submitTransaction() {
    try {
      await clientsApi.addTransaction(clientId, {
        date: form.date,
        pounds: Number(form.pounds),
        income_amount: Number(form.income_amount),
        price_per_pound: Number(form.price_per_pound),
      });
      notify("Transaction added", "success");
      setOpen(false);
      loadTransactions();
    } catch (err) {
      notify(err.apiMessage || "Could not add transaction", "danger");
    }
  }

  async function generateLink() {
    try {
      await shareLinkApi.generate(clientId, 30);
      notify("Share link generated", "success");
      loadShareLinks();
    } catch (err) {
      notify(err.apiMessage || "Could not generate share link", "danger");
    }
  }

  async function revokeLink(linkId) {
    try {
      await shareLinkApi.revoke(clientId, linkId);
      notify("Link revoked", "success");
      loadShareLinks();
    } catch (err) {
      notify(err.apiMessage || "Could not revoke share link", "danger");
    }
  }

  return (
    <div>
      <PageHeader title="Client Transactions">
        <Button onClick={() => setOpen(true)}>Add Transaction</Button>
      </PageHeader>

      <Card className="section-spacing">
        <DataTable
          rows={transactions}
          loading={!transactions}
          rowKey="transaction_id"
          emptyTitle="No transactions yet"
          emptyDescription="Sales and payments for this client appear here as you record them."
          columns={[
            { key: "date", header: "Date", render: (r) => new Date(r.date).toLocaleDateString() },
            { key: "pounds", header: "Pounds" },
            { key: "income_amount", header: "Income", render: (r) => `$${Number(r.income_amount).toFixed(2)}` },
            { key: "invoice_number", header: "Invoice #" },
          ]}
        />
        <Pager
          total={total}
          limit={pageLimit}
          offset={pageOffset}
          unit="transactions"
          onChange={loadTransactions}
        />
      </Card>

      {/* GAP: the token below is real (app.ClientShareLinks, generated/
          revoked server-side) but there's no public, unauthenticated
          "/portal/{token}" route for a client to actually open it yet --
          that's a separate page outside the authenticated app shell
          entirely, not just an endpoint. Add that route before this is a
          real client-facing feature rather than an internal record. */}
      <Card
        title="Client Portal Share Link"
        actions={<Button variant="secondary" size="sm" onClick={generateLink}>Generate New Link</Button>}
      >
        <DataTable
          rows={shareLinks}
          loading={shareLinks === null}
          rowKey="link_id"
          emptyTitle="No active share links"
          emptyDescription="Generate a link so this client can view their own purchase history."
          columns={[
            { key: "token", header: "Token", render: (r) => <code>{r.token}</code> },
            { key: "expires_at", header: "Expires", render: (r) => (r.expires_at ? new Date(r.expires_at).toLocaleDateString() : "—") },
            { key: "access_count", header: "Views" },
            { key: "revoked", header: "Status", render: (r) => (r.revoked ? "Revoked" : "Active") },
            { key: "actions", header: "", render: (r) => !r.revoked && <Button size="sm" variant="danger" onClick={() => revokeLink(r.link_id)}>Revoke</Button> },
          ]}
        />
      </Card>

      <Modal open={open} onClose={() => setOpen(false)} title="Add Transaction" footer={<Button onClick={submitTransaction}>Save</Button>}>
        <div style={{ display: "grid", gap: "var(--space-3)" }}>
          <input id="client-detail-page-date" name="date" type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} className="touch-target" />
          <Field label="Pounds">
            {(id) => <input id={id} type="number" value={form.pounds} onChange={(e) => setForm((f) => ({ ...f, pounds: e.target.value }))} className="touch-target" />}
          </Field>
          <Field label="Income ($)">
            {(id) => <input id={id} type="number" value={form.income_amount} onChange={(e) => setForm((f) => ({ ...f, income_amount: e.target.value }))} className="touch-target" />}
          </Field>
          <Field label="Price per pound">
            {(id) => <input id={id} type="number" value={form.price_per_pound} onChange={(e) => setForm((f) => ({ ...f, price_per_pound: e.target.value }))} className="touch-target" />}
          </Field>
        </div>
      </Modal>
    </div>
  );
}
